/*
 * Endless scrolling for collection and search results.
 *
 * The numbered pagination stays in the HTML — it is what crawlers and
 * no-JavaScript visitors follow, and it is the fallback if a fetch fails.
 * When this script runs it hides those numbers and pulls the next page in as
 * the shopper reaches the bottom, with a "Load more" button underneath for
 * anyone who would rather click.
 */

(function () {
  'use strict';

  // Products, the collections index and the blog all paginate with the same
  // snippet but hang their items off a different list.
  var GRIDS = ['#product-grid', 'ul.collection-list', '.blog-articles'];
  var WRAP = '.pagination-wrapper';

  function gridSelector() {
    for (var i = 0; i < GRIDS.length; i++) {
      var el = document.querySelector(GRIDS[i]);
      if (el && el.children.length) return GRIDS[i];
    }
    return null;
  }

  function pageOf(href) {
    try {
      return parseInt(new URL(href, window.location.origin).searchParams.get('page') || '0', 10) || 0;
    } catch (e) {
      return 0;
    }
  }

  // Dawn's markup calls the next-page arrow "--prev", so find it by page
  // number instead: the lowest page in the list that is above the current one.
  function nextHref(wrapper) {
    var current = parseInt(wrapper.getAttribute('data-page') || '1', 10) || 1;
    var best = null;
    var bestPage = Infinity;
    wrapper.querySelectorAll('a[href]').forEach(function (a) {
      var p = pageOf(a.getAttribute('href'));
      if (p > current && p < bestPage) {
        bestPage = p;
        best = a.href;
      }
    });
    return best;
  }

  // Appended cards must not carry Dawn's reveal-on-scroll classes: its observer
  // is only wired up for elements present at load, so anything added later
  // would stay stuck at opacity 0.
  function unfreeze(item) {
    item.classList.remove('scroll-trigger', 'animate--slide-in');
    item.removeAttribute('data-cascade');
    item.style.removeProperty('--animation-order');
    item.querySelectorAll('.scroll-trigger').forEach(function (el) {
      el.classList.remove('scroll-trigger', 'animate--slide-in');
      el.removeAttribute('data-cascade');
    });
  }

  function setup(wrapper) {
    if (!wrapper || wrapper.dataset.cdInfinite) return;

    var selector = gridSelector();
    if (!selector) return;
    var grid = document.querySelector(selector);
    if (!grid) return;

    // Collection pages give us <ul id="product-grid"> with <li> cards directly
    // inside. Search wraps the same cards in <div id="product-grid"><ul>…</ul>.
    // Work with the list itself in both cases, or nothing gets appended.
    function listOf(root) {
      if (!root) return null;
      if (root.tagName === 'UL' || root.tagName === 'OL') return root;
      return root.querySelector('ul, ol') || root;
    }
    var list = listOf(grid);
    if (!nextHref(wrapper)) return;

    // Shopify's sort is not stable across page fetches: when several products
    // share a sort value, one at the end of page N can come back at the top of
    // page N+1. Appending blindly shows it twice, so keep a note of what is
    // already on screen and skip anything that arrives a second time.
    var seen = Object.create(null);
    function keyOf(li) {
      var a = li.querySelector('a[href*="/products/"]');
      if (!a) return null;
      return a.getAttribute('href').split('?')[0].split('/products/').pop();
    }
    Array.prototype.forEach.call(list.children, function (li) {
      var k = keyOf(li);
      if (k) seen[k] = true;
    });

    wrapper.dataset.cdInfinite = 'on';
    wrapper.classList.add('cd-infinite');

    var status = document.createElement('div');
    status.className = 'cd-infinite__status';
    var isProducts = selector === '#product-grid';
    status.innerHTML =
      '<button type="button" class="cd-infinite__button button button--secondary">Load more</button>' +
      '<p class="cd-infinite__loading" hidden>Loading more…</p>' +
      '<p class="cd-infinite__done" hidden>' +
      (isProducts ? 'You have reached the end of this collection.' : 'That is every category.') +
      '</p>';
    // A filter or sort change makes Dawn rebuild the grid and the pagination,
    // leaving the previous status block orphaned in the page — which is how you
    // end up seeing "Load more" and "end of this collection" at the same time.
    Array.prototype.forEach.call(
      wrapper.parentNode.querySelectorAll('.cd-infinite__status'),
      function (old) { old.remove(); }
    );
    wrapper.parentNode.insertBefore(status, wrapper);

    var button = status.querySelector('.cd-infinite__button');
    var loading = status.querySelector('.cd-infinite__loading');
    var done = status.querySelector('.cd-infinite__done');
    var busy = false;
    var failures = 0;

    function finish() {
      button.hidden = true;
      loading.hidden = true;
      done.hidden = false;
      if (observer) observer.disconnect();
    }

    function giveUp() {
      // Put the numbers back rather than stranding the shopper mid-collection.
      wrapper.classList.remove('cd-infinite');
      status.remove();
      if (observer) observer.disconnect();
    }

    function load() {
      if (busy) return;
      var url = nextHref(wrapper);
      if (!url) return finish();

      busy = true;
      button.hidden = true;
      loading.hidden = false;

      fetch(url)
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var incoming = doc.querySelector(selector);
          var newWrapper = doc.querySelector(WRAP);
          if (!incoming) throw new Error('no list in the response');

          var incomingList = listOf(incoming);
          var items = incomingList.querySelectorAll(':scope > li');
          items.forEach(function (li) {
            var k = keyOf(li);
            if (k) {
              if (seen[k]) return;
              seen[k] = true;
            }
            unfreeze(li);
            list.appendChild(li);
          });

          if (newWrapper) {
            wrapper.setAttribute('data-page', newWrapper.getAttribute('data-page') || '');
            var nav = wrapper.querySelector('.pagination');
            var newNav = newWrapper.querySelector('.pagination');
            if (nav && newNav) nav.replaceWith(newNav);
          }

          try {
            window.history.replaceState({}, '', url);
          } catch (e) {
            /* replaceState can throw in odd embeds; not worth failing over */
          }

          busy = false;
          loading.hidden = true;
          failures = 0;

          if (nextHref(wrapper)) button.hidden = false;
          else finish();
        })
        .catch(function () {
          busy = false;
          loading.hidden = true;
          failures++;
          if (failures >= 2) giveUp();
          else button.hidden = false;
        });
    }

    button.addEventListener('click', load);

    var observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          if (entries[0].isIntersecting) load();
        },
        { rootMargin: '800px 0px' }
      );
      observer.observe(status);
    }
  }

  function scan() {
    document.querySelectorAll(WRAP).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Dawn rebuilds the whole grid when a filter or sort changes, which throws
  // away our button and brings back a fresh pagination block.
  var rescan;
  new MutationObserver(function () {
    clearTimeout(rescan);
    rescan = setTimeout(scan, 120);
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', scan);
})();
